import React, { memo, useRef } from 'react';
import { useStyles } from 'uinix-ui';

import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';

import { useClickOutside } from '../../hooks/useClickOutside';
import { useEscape } from '../../hooks/useEscape';
import { theme } from '../../system/theme';
import { SharedPageOverlayProps } from '../../types/SharedPageOverlayProps';
import { SharedPanelProps } from '../../types/SharedPanelProps';
import { Backdrop } from '../Backdrop/Backdrop';
import { Box } from '../Box/Box';
import { ContentContainer } from '../ContentContainer/ContentContainer';
import { Layout } from '../Layout/Layout';
import { Panel } from '../Panel/Panel';
import { Portal } from '../Portal/Portal';

interface Props extends SharedPageOverlayProps {
  /** Optional overlay footer */
  footer?: React.ReactNode;
  /** Optional overlay header */
  header?: React.ReactNode;
  /** Optional panel (only supported when `isFullPage=true` */
  panel?: SharedPanelProps;
}

/**
 * `PageOverlay` is a controlled component that renders popover content with action buttons.
 *
 * `PageOverlay` visibiliy behaviors
 * - `isVisible` controls the visibility
 * - `onHide` callback is called when the overlay is being hidden.
 */
export const PageOverlay = memo(
  ({
    backgroundGradient,
    children,
    disableHideOnClickOutside,
    disableHideOnEscape,
    enableContentPadding = true,
    footer,
    header,
    isFullPage = false,
    isStretched = false,
    isVisible = false,
    renderBelowHeader = false,
    loadingContent,
    overflow = 'auto',
    panel,
    placeholderContent,
    width = 'm',
    onHide,
  }: Props) => {
    const showHeaderForFullscreenModals = useFlag(
      FlagType.ShowHeaderForFullscreenModals,
    );
    const overlayRef = useRef(null);

    const styles = useStyles();

    const enableHideOnEscape = isVisible && !disableHideOnEscape;

    const p = enableContentPadding ? 4 : undefined;

    const handleHide = () => {
      onHide?.();
    };

    const handleHideOnClickOutside = () => {
      if (!disableHideOnClickOutside) {
        handleHide();
      }
    };

    useClickOutside(overlayRef, handleHideOnClickOutside);

    useEscape(handleHide, enableHideOnEscape);

    if (!isVisible) {
      return null;
    }

    let bodyLayoutStyle = null;
    if (isFullPage) {
      bodyLayoutStyle = componentStyles.bodyFullPage;
    } else if (isStretched) {
      bodyLayoutStyle = componentStyles.bodyStretch;
    }

    const ui = (
      <Backdrop
        disableBodyScroll={isVisible}
        mode="center"
        topOffset={
          isFullPage && (renderBelowHeader || showHeaderForFullscreenModals)
            ? (theme.sizes['header.height.l'] as number)
            : 0
        }
      >
        <div
          ref={overlayRef}
          style={componentStyles.overlay({ isFullPage, width })}
        >
          <Layout
            aria-modal
            direction="column"
            role="dialog"
            styles={componentStyles.container({ isFullPage })}
            variant={isFullPage ? undefined : 'borderRadius.all'}
          >
            {header}
            <Layout
              styles={[
                componentStyles.body({ overflow }),
                bodyLayoutStyle,
                isFullPage && backgroundGradient
                  ? styles.background.gradient[backgroundGradient]
                  : null,
              ]}
            >
              {panel?.position === 'left' && (
                <Layout flex="none">
                  <Panel {...panel} />
                </Layout>
              )}
              <ContentContainer
                loadingContent={loadingContent}
                placeholderContent={placeholderContent}
              >
                <Layout
                  direction="column"
                  flex="auto"
                  overflowY={overflow}
                  p={p}
                >
                  {children}
                </Layout>
              </ContentContainer>
              {panel && panel.position !== 'left' && (
                <Layout flex="none">
                  <Panel {...panel} />
                </Layout>
              )}
            </Layout>
            {footer && (
              <Box
                bg="background"
                borderTop="border.divider"
                px={p}
                variant={isFullPage ? undefined : 'borderRadius.bottom'}
              >
                {footer}
              </Box>
            )}
          </Layout>
        </div>
      </Backdrop>
    );

    return <Portal>{ui}</Portal>;
  },
);

const offsetMargin = theme.spacings[12] as number;

const getBodyMaxHeight = () => {
  const headerHeight = theme.sizes['header.height'] as number;
  const bodyMaxHeight = theme.sizes['page-overlay.body.max-height'] as number;
  const footerHeight = theme.sizes['footer.height'] as number;
  return `min(calc(100vh - ${2 * offsetMargin}px - ${
    headerHeight + footerHeight
  }px), ${bodyMaxHeight}px)`;
};

const componentStyles = {
  body: ({ overflow }: { overflow: SharedPageOverlayProps['overflow'] }) => ({
    flex: 'auto',
    overflowY: overflow,
    maxHeight: getBodyMaxHeight(),
  }),
  bodyStretch: {
    height: getBodyMaxHeight(),
  },
  bodyFullPage: {
    maxHeight: 'unset',
  },
  container: ({ isFullPage }: { isFullPage: boolean }) => ({
    backgroundColor: 'background',
    boxShadow: 'raised',
    flex: 'auto',
    height: '100%',
    marginBottom: isFullPage ? 0 : offsetMargin,
    marginTop: isFullPage ? 0 : offsetMargin,
    minWidth: 0,
    width: '100%',
  }),
  overlay: ({
    isFullPage,
    width,
  }: {
    isFullPage: boolean;
    width: SharedPanelProps['width'];
  }) => {
    let overlayWidth;
    if (isFullPage) {
      overlayWidth = '100vw';
    } else if (width === 'fit-content') {
      overlayWidth = 'fit-content';
    } else {
      overlayWidth = theme.sizes[`page-overlay.${width}`];
    }

    return {
      display: 'flex',
      height: isFullPage ? '100%' : 'fit-content',
      maxWidth: '100vw',
      width: overlayWidth,
    } as object;
  },
  paginate: {
    marginBottom: 'auto',
    marginTop: 'auto',
    paddingLeft: 3,
    paddingRight: 3,
    '> button': {
      backgroundColor: 'white',
      borderRadius: '50%',
      boxShadow: 'raised',
    },
  },
};
